'use client';
import React, { useEffect, useState } from 'react';
import Add from '@carbon/icons-react/es/Add.js';
import ChevronDown from '@carbon/icons-react/es/ChevronDown.js';
import { Button } from '@carbon/react';
import {
  getContentEngagementStatus,
  postContentEngagementDetail,
} from '@/apis/ContentEngagementAPIs';
import Notification from '../Notification';
import { checkAuthentication, isAuthenticated } from '@/utils/helper';

type NotificationType = 'info' | 'success' | 'error';

interface NotificationState {
  message: string;
  type: NotificationType;
  isVisible: boolean;
}

interface FollowButtonProps {
  handleClick?: () => void;
  handleDeleteClick?: () => void;
  text: string;
  color?:
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'ghost'
    | 'danger--primary'
    | 'danger--ghost'
    | 'danger--tertiary'
    | 'tertiary';
  ariaLabel?: string;
  href?: string;
  target?: string;
  slug?: string;
  content_type?: string;
  title?: string;
}

export const FollowButton: React.FC<FollowButtonProps> = ({
  text,
  color = 'primary',
  ariaLabel,
  href,
  target,
  slug,
  content_type,
  title,
}) => {
  const [isFollowed, setIsFollowed] = useState(false);

  const [notification, setNotification] = useState<NotificationState>({
    message: '',
    type: 'info',
    isVisible: false,
  });

  async function getEngagementStatus(slug: string) {
    let engagementData = await getContentEngagementStatus(slug);
    engagementData = engagementData?.body;
    setIsFollowed(engagementData?.['follow']);
  }

  useEffect(() => {
    getEngagementStatus(slug ?? '');
  }, [slug]);

  const followButtonClick = async (skipAuthCheck = false) => {
    const isLoggedIn = skipAuthCheck ? isAuthenticated() : checkAuthentication();

    if (!isLoggedIn) {
      sessionStorage.setItem('pendingEngagementAction', isFollowed ? 'unfollow' : 'follow');
      sessionStorage.setItem('pendingEngagementSlug', slug ?? '');
      return;
    }

    const postRes = await postContentEngagementDetail(
      slug ?? '',
      content_type ?? '',
      'following',
      title ?? '',
      isFollowed,
    );

    if (postRes?.status === 200) {
      setIsFollowed(prev => !prev);
      setNotification({
        message: !isFollowed ? 'Content followed' : 'Deleted following.',
        type: 'success',
        isVisible: true,
      });
    } else {
      setNotification({
        message: 'An error occured while following.',
        type: 'error',
        isVisible: true,
      });
    }
  };

  useEffect(() => {
    const handlePendingFollowAction = async () => {
      if (typeof window === 'undefined') return;

      const pendingAction = sessionStorage.getItem('pendingEngagementAction');
      const pendingSlug = sessionStorage.getItem('pendingEngagementSlug');

      if (pendingSlug === (slug ?? '') && isAuthenticated()) {
        if (pendingAction === 'follow' && !isFollowed) {
          sessionStorage.removeItem('pendingEngagementAction');
          sessionStorage.removeItem('pendingEngagementSlug');
          await followButtonClick(true);
        } else if (pendingAction === 'unfollow' && isFollowed) {
          sessionStorage.removeItem('pendingEngagementAction');
          sessionStorage.removeItem('pendingEngagementSlug');
          await followButtonClick(true);
        }
      }
    };

    handlePendingFollowAction();
  }, [slug, isFollowed]);

  return (
    <div>
      {/* Show Notification component here */}
      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={() => {
          setNotification({
            message: '',
            type: 'info',
            isVisible: false,
          });
        }}
      />

      <Button
        kind={color}
        renderIcon={isFollowed ? ChevronDown : Add}
        iconDescription={ariaLabel || text}
        href={href}
        target={target}
        onClick={() => {
          void followButtonClick();
        }}
        data-testid={'global-follow-button'}
      >
        <span>{text}</span>
      </Button>
    </div>
  );
};
